import { customEvent } from "src/utils/gtag.js";

/**
 * A/B Testing Configuration
 * Add new experiments here with their variants and conditions
 */
const AB_EXPERIMENTS = {
  // Free User Subscription Flow Experiment  
  FREE_USER_SUBSCRIPTION_FLOW: {
    name: "free_subscription_flow",
    description: "Test different subscription experiences for free users",
    active: true,
    // Targeting conditions
    conditions: {
      userHasValidSubscription: false, // Target free users only
    },
    // Experiment variants
    variants: [
      {
        name: "calendar_experience", // Control - existing flow
        route: "SUBSCRIBING_CALENDAR",
        weight: 100, // 50% of users
        description: "Original calendar experience flow"
      },
      {
        name: "subscription_experience", // Treatment
        route: "SUBSCRIBING_CALENDAR_EXPERIENCE", 
        weight: 0, // 50% of users
        description: "New subscription experience flow"
      }
    ],
    // Analytics configuration
    analytics: {
      experimentEvent: "ab_test_assignment",
      conversionEvents: [
        "calendar_added_successfully",          // Calendar actually gets added to user's account  
        "trial_period_payment"                  // User converts to paid
      ]
    }
  },

  // Example of another experiment (commented out)
  // UPGRADE_BUTTON_TEXT: {
  //   name: "upgrade_button_text",
  //   description: "Test different upgrade button copy",
  //   active: false,
  //   conditions: {
  //     userHasValidSubscription: false,
  //   },
  //   variants: [
  //     { name: "control", text: "BECOME A MEMBER TODAY", weight: 33 },
  //     { name: "urgency", text: "UPGRADE NOW - LIMITED TIME", weight: 33 },
  //     { name: "benefit", text: "UNLOCK ALL FEATURES", weight: 34 }
  //   ]
  // }
};

/**
 * A/B Testing Service Class
 */
class ABTestingService {
  constructor() {
    this.assignments = new Map(); // Cache user assignments
  }

  /**
   * Get user assignment for a specific experiment
   * @param {string} experimentKey - The experiment identifier
   * @param {Object} user - User object
   * @param {Object} state - Additional state (calendar, etc.)
   * @returns {Object} Assignment result with variant and tracking data
   */
  getAssignment(experimentKey, user, state = {}) {
    const experiment = AB_EXPERIMENTS[experimentKey];
    
    if (!experiment || !experiment.active) {
      return { variant: null, experimentKey, assigned: false };
    }

    // Check if user meets targeting conditions
    if (!this.meetsConditions(experiment.conditions, user, state)) {
      return { variant: null, experimentKey, assigned: false };
    }

    // Check cache first
    const cacheKey = `${experimentKey}_${user._id}`;
    if (this.assignments.has(cacheKey)) {
      return this.assignments.get(cacheKey);
    }

    // Assign user to variant
    const variant = this.assignUserToVariant(experiment, user);
    const assignment = {
      variant,
      experimentKey,
      experimentName: experiment.name,
      assigned: true,
      timestamp: new Date().toISOString()
    };

    // Cache the assignment
    this.assignments.set(cacheKey, assignment);

    // Track assignment in GA
    this.trackAssignment(assignment, user, state);

    return assignment;
  }

  /**
   * Check if user meets experiment conditions
   * @param {Object} conditions - The conditions to check (e.g. { userHasValidSubscription: false })
   * @param {Object} user - The user to check
   * @param {Object} state - Additional state (calendar, etc.) - currently unused but available for future conditions
   * @returns {boolean} True if the user meets the conditions, false otherwise
   */
  meetsConditions(conditions, user, state) {
    for (const [key, expectedValue] of Object.entries(conditions)) {
      switch (key) {
        case 'userHasValidSubscription':
          if (user.hasValidSubscription !== expectedValue) return false;
          break;
        case 'userProvider':        // example condition: user.provider === "google"
          if (user.provider !== expectedValue) return false;
          break;
        // Add more conditions as needed
        default:
          console.warn(`Unknown condition: ${key}`);
      }
    }
    return true;
  }

  /**
   * Assign user to variant using consistent hashing
   */
  assignUserToVariant(experiment, user) {
    // Create deterministic hash from user ID and experiment name
    const hash = this.hashString(`${user._id}_${experiment.name}`);
    const bucket = hash % 100; // 0-99

    // Assign based on cumulative weights
    let cumulative = 0;
    for (const variant of experiment.variants) {
      cumulative += variant.weight;
      if (bucket < cumulative) {
        return variant;
      }
    }

    // Fallback to first variant
    return experiment.variants[0];
  }

  /**
   * Simple hash function for consistent user assignment
   */
  hashString(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash);
  }

  /**
   * Track experiment assignment in Google Analytics
   */
  trackAssignment(assignment, user, state = {}) {
    customEvent({
      name: `${assignment.experimentName}_${assignment.variant.name}`,
      category: "AB_Test",
      label: `${assignment.experimentKey}: ${assignment.variant.name}`,
      data: {
        user_id: user._id,
        user_email: user.email,
        experiment_name: assignment.experimentName,
        variant_name: assignment.variant.name,
        assignment_timestamp: assignment.timestamp
      },
    });
  }

  /**
   * Track conversion events for A/B tests
   */
  trackConversion(experimentKey, eventName, user, additionalData = {}) {
    const cacheKey = `${experimentKey}_${user._id}`;
    const assignment = this.assignments.get(cacheKey);

    if (!assignment) {
      console.warn(`No A/B test assignment found for user ${user._id} and experiment ${experimentKey}`);
      return;
    }

    customEvent({
      name: `${assignment.experimentName}_conversion_${eventName}`,
      category: "AB_Test_Conversion", 
      label: `${assignment.experimentKey}: ${assignment.variant.name} -> ${eventName}`,
      data: {
        user_id: user._id,
        user_email: user.email,
        experiment_key: assignment.experimentKey,
        variant_name: assignment.variant.name,
        conversion_event: eventName,
        conversion_timestamp: new Date().toISOString(),
        ...additionalData
      },
    });
  }

  /**
   * Track conversion events for ALL active experiments a user is in
   * Call this from centralized places like useSubscribe
   */
  trackConversionForAllExperiments(eventName, user, additionalData = {}) {
    // Check all active experiments for this user
    for (const [experimentKey, experiment] of Object.entries(AB_EXPERIMENTS)) {
      if (!experiment.active) continue;
      
      const cacheKey = `${experimentKey}_${user._id}`;
      const assignment = this.assignments.get(cacheKey);
      
      if (assignment) {
        // User is in this experiment, track the conversion
        this.trackConversion(experimentKey, eventName, user, additionalData);
      }
    }
  }

  /**
   * Get all active experiments for a user (for debugging)
   */
  getActiveExperiments(user) {
    const active = [];
    for (const [key, experiment] of Object.entries(AB_EXPERIMENTS)) {
      if (experiment.active && this.meetsConditions(experiment.conditions, user, {})) {
        active.push({
          key,
          name: experiment.name,
          description: experiment.description
        });
      }
    }
    return active;
  }

  /**
   * Force assign user to specific variant (for testing)
   */
  forceAssignment(experimentKey, variantName, user) {
    const experiment = AB_EXPERIMENTS[experimentKey];
    if (!experiment) return null;

    const variant = experiment.variants.find(v => v.name === variantName);
    if (!variant) return null;

    const assignment = {
      variant,
      experimentKey,
      experimentName: experiment.name,
      assigned: true,
      forced: true,
      timestamp: new Date().toISOString()
    };

    this.assignments.set(`${experimentKey}_${user._id}`, assignment);
    return assignment;
  }
}

// Export singleton instance
export const abTestingService = new ABTestingService();
export default abTestingService; 